import { FC, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";

import FooterText from "../../components/footerText";
import HubInputField from "../../components/inputType";
import AuthSubHeader from "../../components/authSubHeader";
import BAAContent from "src/components/baaContent";

import useFetch from "../../hooks/useFetch";
import { ToastContext } from "../../context/toastContext";
import { AuthContext } from "../../context/authContext";

import { DEFAULT_MANUFACTURE_PAGE_SELECTION, DEFAULT_PAGE_SELECTION, DEFAULT_REF_PAGE_SELECTION, USER_TYPES } from "src/constants";
import { createUserAPIResponseType, InitialUserFormType } from "./types";
import { APIEndpoints } from "../../types/apiTypes";
import { ToastTypes } from "../../types";
import { LoaderContext } from "src/context/loaderContext";
import { InitialUserSchema } from "src/validation/initalUser";
import { checkLandingPageExists, isNotKAMUser } from "src/helpers";
import { CreateUserSchema } from "src/validation/createUser";



const HubInitialUser: FC = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isManufacturer, userData, defaultView, updateUserData, reinvestigation, outreach, pcc_outreach } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const schema = userData?.is_password_changed
    ? CreateUserSchema
    : InitialUserSchema;
  const { control, handleSubmit, setValue } = useForm<InitialUserFormType>({
    mode: 'all',
    resolver: yupResolver(schema),
  });



  useEffect(() => {
    if (userData) {
      setValue("firstname", userData.firstname);
      setValue("middlename", userData.middlename || "");
      setValue("lastname", userData.lastname);
      setValue("email", userData.email);
      defaultView && userData?.is_password_changed && setValue("default_view", defaultView);
      setValue("reinvestigations", reinvestigation)
      setValue("outreach", outreach)
      setValue("pcc_outreach", pcc_outreach)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // useEffect (()=>{
  //   if(defaultView){


  //   }else if(reinvestigation){
  //     setValue("reinvestigations",reinvestigation)

  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // },[defaultView,reinvestigation])

  const { loading: userLoading, data: updateUserApiResponse, mutate: addNewUser, error, removeErrorAndData } =
    useFetch<createUserAPIResponseType>(APIEndpoints.USER_UPDATE, {}, { userId: userData?.id });

  useEffect(() => {
    hideLoader();
    if (updateUserApiResponse) {
      // console.log('updateUserApiResponse.....',updateUserApiResponse,"  ",defaultView)
      const { code, message, data } = updateUserApiResponse.status;

      if (code === 200) {
        if (data) {
          const { user, navbar } = data;

          if (user && navbar) {
            const landingPage = navbar.landing_page.default_view || defaultView
            updateUserData(
              user,
              landingPage,
              navbar?.reinvestigation === "1" || reinvestigation === true ? true : false,
              navbar?.outreach === "1" || navbar?.outreach === true ? true : false, 
              navbar?.pcc_outreach === "1" || navbar?.pcc_outreach === true ? true : false
            );

            if (location.pathname.includes("update")) {
              showToast([message], ToastTypes.SUCCESS);
            } else {
              if (isManufacturer && userData) {
                if (userData.ref_type === "area_manager") {
                  if (landingPage === "statistics") {
                    navigate(`/areas/${userData.id}?id=${userData.id}&type=areas&role=area_manager`);
                  } else {
                    navigate(`/${landingPage}?id=${userData.id}&role=area_manager`);
                  }
                } else if (userData.ref_type === "regional_manager") {
                  if (landingPage === "statistics") {
                    navigate(`/regions/${userData.id}?id=${userData.id}&type=regions&role=regional_manager`)
                  } else {
                    navigate(`/${landingPage}?id=${userData.id}&role=regional_manager`)
                  }
                }
                else if (userData.ref_type === "district_manager") {
                  if (landingPage === "statistics") {
                    navigate(`/district/${userData.id}?id=${userData.id}&type=district&role=district_manager`)
                  } else {
                    navigate(`/${landingPage}?id=${userData.id}&role=district_manager`)
                  }
                }
                else if (userData.ref_type === "rep") {
                  if (landingPage === "statistics") {
                    navigate(`/territories/${userData.id}?id=${userData.id}&role=rep&type=territories`)
                  } else {
                    navigate(`/${landingPage}?id=${userData.id}&role=rep&type=territories`)
                  }
                } else {
                  navigate(`/${landingPage}`);
                }
              }
              else
                if (userData && USER_TYPES.MANUFACTURER.includes(user.ref_type)) {
                  if (checkLandingPageExists(userData.ref_type, landingPage)) {
                    navigate(`/${landingPage}`)
                  } else {
                    navigate(`/${'todos_statistics'}`);
                  }

                } else if (userData && USER_TYPES.HCP.includes(userData.ref_type)) {
                  if (checkLandingPageExists(userData.ref_type, landingPage)) {
                    navigate(`/${landingPage}`)
                  } else { navigate(`/${`cases`}`); }
                }
            }
          }
        }
      } else if (code === 500) {
        showToast([message], ToastTypes.ERROR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserApiResponse]);

  const submitForm = async (values: InitialUserFormType) => {
    setLoader()
    if ((error && error.length > 0) || updateUserApiResponse) removeErrorAndData();

    const { default_view, reinvestigations, outreach, pcc_outreach, ...requiredValues } = values;
    //console.log('updateUserApiResponse.....',values,"  ",defaultView)

    const userValues = {
      ...requiredValues,
      practice_attributes: { 
        reinvestigations: {
          name: 'reinvestigations', value: reinvestigations
        },
        outreaches: {
          name: 'outreaches', value: outreach
        },
        pcc_outreaches: {
          name: 'pcc_outreaches', value: pcc_outreach
        },
      },
      user_attributes: {
        name: "default_view",
        value: values.default_view,
      }
    }
    // console.log({ user: userValues });
    addNewUser({ user: userValues });
    userData && default_view && reinvestigations && 
      updateUserData(userData, default_view, reinvestigations, outreach ?? false, pcc_outreach ?? false);
  };

  return (
    <div className="container-fluid overflow-y-auto overflow-x-auto d-flex flex-column flex-fill">
      <div className={isManufacturer ? "" : "container container-short"}>
        <AuthSubHeader />
        <div className="card mt-3">
          <div className="card-header bg-success-subtle">
            <h3 className="text-black m-0">
              {userData?.is_password_changed ? "Edit Account" : "Initial Login and Password Change"}
            </h3>
          </div>

          <div className="card-body">
            <form onSubmit={handleSubmit(submitForm)}>
              {
                <p className="fs-6">
                  {location.pathname.includes("edit") ?
                    "You may edit your user account information here. Note that by using this platform, you accept and agree to the terms of the Business Associates Agreement below."
                    : "For security verification, please update your password before accessing the portal."
                  }
                </p>
              }
              <div className="mb-3">
                <Controller
                  name="firstname"
                  control={control}
                  defaultValue={userData?.firstname}
                  // defaultValue=''
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="firstname"
                      isRowBased={true}
                      isRequired={true}
                      field={field}
                      error={error}
                      inputLabel="First Name"
                      inputType="firstName"
                    />
                  )}
                />
              </div>

              <div className="mb-3">
                <Controller
                  name="middlename"
                  control={control}
                  defaultValue={userData?.middlename || ""}
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="middlename"
                      isRowBased={true}
                      isRequired={false}
                      field={field}
                      error={error}
                      inputLabel="Middle Name"
                      inputType="middleName"
                    />
                  )}
                />
              </div>

              <div className="mb-3">
                <Controller
                  name="lastname"
                  control={control}
                  defaultValue={userData?.lastname}
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="lastname"
                      isRowBased={true}
                      isRequired={true}
                      field={field}
                      error={error}
                      inputLabel="Last Name"
                      inputType="lastName"
                    />
                  )}
                />
              </div>

              <div className="mb-3">
                <Controller
                  name="email"
                  control={control}
                  // defaultValue=''
                  defaultValue={userData?.email}
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="email"
                      isRowBased={true}
                      isRequired={true}
                      field={field}
                      error={error}
                      inputLabel="Email"
                      inputType="email"
                    />
                  )}
                />
              </div>

              <div className="card my-3">
                <div className="card-header bg-success-subtle">
                  <h3 className="text-black m-0">Account Settings</h3>
                </div>

                <div className="card-body">
                  {!isManufacturer ? (
                    <>
                      <Controller
                        name="reinvestigations"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="reinvestigations"
                              checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <label className="form-check-label" htmlFor="reinvestigations">
                              Automatically reinvestigate patients for ongoing treatment
                            </label>
                          </div>
                        )}
                      />
                      <Controller
                        name="outreach"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="outreach"
                              checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <label className="form-check-label" htmlFor="outreach">
                              Automatically initiate outreach by a QUTENZA Nurse Specialist who will contact the patient directly 
                              to provide education and support specific to treatment with QUTENZA. Patients will be asked to 
                              provide additional consent to proceed with the program and may opt-out at any time.
                            </label>
                          </div>
                        )}
                      />
                      <Controller
                        name="pcc_outreach"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="pcc_outreach"
                              checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <label className="form-check-label" htmlFor="pcc_outreach">
                              Automatically initiate outreach by a Patient Care Coordinator who will contact patients 
                              directly to provide insurance coverage details specific to treatment with QUTENZA three 
                              days after the Benefits Investigation is completed by My Qutenza Connect. Patients will be 
                              asked to provide additional consent to proceed with the program and may opt-out at any time
                            </label>
                          </div>
                        )}
                      />
                      <br />
                    </>
                  ) : null}
                  <Controller
                    name="default_view"
                    control={control}
                    defaultValue={
                      (isManufacturer
                        ? DEFAULT_MANUFACTURE_PAGE_SELECTION[0]?.value
                        : DEFAULT_PAGE_SELECTION[0]?.value) || ''
                    }
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="default_view"
                        isRowBased={true}
                        isRequired={false}
                        field={field}
                        error={error}
                        inputLabel="Select Default Landing Page"
                        isDropdown={true}
                        isSearchable={false}
                        dropdownData={
                          (isManufacturer ? 
                            isNotKAMUser(userData?.ref_type ?? "") ? 
                              DEFAULT_MANUFACTURE_PAGE_SELECTION :  
                                DEFAULT_REF_PAGE_SELECTION :  
                              DEFAULT_PAGE_SELECTION
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                      />
                    )}
                  />
                </div>
              </div>

              <div className="card my-3">
                <div className="card-header bg-success-subtle">
                  <h3 className="text-black m-0">Password</h3>
                </div>

                <div className="card-body">
                  <p>Password must be at least 10 characters long (no spaces) and include at least one uppercase
                    letter, at least three lowercase letters, one special character, and a number.</p>

                  <div className="mb-3">
                    <Controller
                      name="password"
                      control={control}
                      defaultValue=''
                      render={({ field, fieldState: { error } }) => (
                        <HubInputField
                          inputId="password"
                          isRowBased={true}
                          isRequired={true}
                          field={field}
                          error={error}
                          inputLabel="Password"
                          inputType="password"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-3">
                    <Controller
                      name="password_confirmation"
                      control={control}
                      defaultValue=''
                      render={({ field, fieldState: { error } }) => (
                        <HubInputField
                          inputId="password_confirmation"
                          isRowBased={true}
                          isRequired={true}
                          field={field}
                          error={error}
                          inputLabel="Confirm Password"
                          inputType="password"
                        />
                      )}
                    />
                  </div>

                </div>
              </div>

              <div className="card my-3">
                <div className="card-header bg-success-subtle">
                  <h3 className="text-black mb-0">Business Associate Agreement</h3>
                </div>

                <div className="card-body overflow-auto" style={{ maxHeight: '30rem' }}>
                  <BAAContent />
                </div>
              </div>

              <button className="btn btn-blue m-1 mt-2" type="submit">
                {userLoading ? <i className="spinner-border me-2" style={{ width: 16, height: 16, fontSize: 9 }}></i> : null}
                Save
              </button>
            </form>
          </div>
        </div>

        <div className="mt-4"><FooterText /></div>
      </div>
    </div>
  );
};

export default HubInitialUser;
